<template>
  <div class="location-mini-map">
    <div class="map-frame">
      <b-skeleton-img v-if="loading" no-aspect class="map-skeleton"></b-skeleton-img>
      <div
        v-if="loadError"
        class="map-error d-flex align-items-center justify-content-center flex-column text-center px-3"
        data-testid="mini-map-error"
      >
        <span class="mb-2">{{ $t('map_load_error') }}</span>
        <button type="button" class="btn btn-sm btn-outline-secondary" data-testid="mini-map-retry" @click="retry">
          {{ $t('try_again') }}
        </button>
      </div>
      <div ref="map" class="map-canvas"></div>
    </div>
    <div class="map-legend mt-2 font-size-xSmall">
      <span class="legend-chip">
        <span class="legend-dot legend-region"></span>
        {{ regionLegend }}
      </span>
      <span v-if="pins.length > 0" class="legend-chip">
        <span class="legend-dot legend-pin"></span>
        {{ pins.length }} {{ $t('cities') }}
      </span>
    </div>
  </div>
</template>

<script>
import { Loader } from '@googlemaps/js-api-loader'
import brazilStates from './brazil-states.json'

const MAX_PINS = 50
// Tempos máximos: o mapa é só visual, então não pode deixar o skeleton girando indefinidamente
const GOOGLE_LOAD_TIMEOUT_MS = 15000
const GEOCODE_TIMEOUT_MS = 8000
const GOLD = '#d8ab44'
const GOLD_DARK = '#a67c1d'
const WORLD_BOUNDS = { north: 80, south: -58, east: 180, west: -170 }
// Cache de geocoding (lat/lng só vêm do backend quando preenchidos), compartilhado entre instâncias
const geocodeCache = new Map()
let worldGeoJson = null

/**
 * Mini mapa somente visual (Google Maps, mesma lib do mapa "por raio").
 * Recebe os itens crus devolvidos pela API (addresses/countries|states|cities)
 * e não altera nenhum dado do formulário.
 */
export default {
  name: 'LocationMiniMap',
  props: {
    // [{ short_name, name }]
    countries: { type: Array, default: () => [] },
    // [{ short_name, name, country_short_name }]
    states: { type: Array, default: () => [] },
    // [{ id, name, state_short_name, country_short_name, lat?, lng?, population? }]
    cities: { type: Array, default: () => [] }
  },
  data () {
    return {
      loading: true,
      loadError: false,
      pins: []
    }
  },
  computed: {
    isBrazilOnly () {
      return this.countries.length === 1 && this.countries[0].short_name === 'BR'
    },
    // Códigos pintados: UFs do Brasil ou ISO-2 dos países
    selectedCodes () {
      if (this.isBrazilOnly) {
        const ufs = this.states.filter(s => s.country_short_name === 'BR').map(s => s.short_name)
        // Brasil sem estados escolhidos = país inteiro selecionado
        return ufs.length > 0 ? ufs : brazilStates.features.map(f => f.properties.uf)
      }
      return this.countries.map(c => c.short_name)
    },
    regionLegend () {
      return this.isBrazilOnly && this.states.length > 0
        ? `${this.states.length} ${this.$t('states')}`
        : `${this.countries.length} ${this.$t('countries')}`
    },
    signature () {
      return JSON.stringify([this.isBrazilOnly, this.selectedCodes, this.cities.map(c => c.id)])
    }
  },
  watch: {
    signature () {
      this.render()
    }
  },
  mounted () {
    this.renderToken = 0
    this.markers = []
    this.init()
  },
  beforeDestroy () {
    this.renderToken++
    if (this.resizeObserver) this.resizeObserver.disconnect()
    this.clearMarkers()
    this.map = null
  },
  methods: {
    // Rejeita se a promise não resolver no prazo
    withTimeout (promise, ms) {
      let timer
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ms)
      })
      return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
    },
    retry () {
      this.loadError = false
      this.loading = true
      this.init()
    },
    async init () {
      // Mesmas opções do GoogleMapsComponent (o Loader exige opções idênticas).
      // Marca d'água "For development purposes only" = configuração da chave no Google Cloud, não do código.
      const loader = new Loader({
        apiKey: process.env.VUE_APP_GCP_API_KEY,
        version: 'weekly',
        libraries: ['places', 'marker', 'geometry']
      })
      try {
        this.google = await this.withTimeout(loader.load(), GOOGLE_LOAD_TIMEOUT_MS)
      } catch (e) {
        if (this._isDestroyed) return
        this.loading = false
        this.loadError = true
        return
      }
      if (this._isDestroyed) return
      this.setupMap()
    },
    setupMap () {
      if (this.map) return
      this.map = new this.google.maps.Map(this.$refs.map, {
        center: { lat: -14.2, lng: -51.9 },
        zoom: 3,
        mapId: process.env.VUE_APP_GOOGLE_MAPS_MAP_ID,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'cooperative',
        clickableIcons: false,
        minZoom: 2
      })
      this.infoWindow = new this.google.maps.InfoWindow({ disableAutoPan: true })
      this.map.data.addListener('mouseover', e => {
        this.map.data.overrideStyle(e.feature, { strokeWeight: 2, strokeColor: GOLD_DARK })
        this.infoWindow.setContent(`<span class="mini-map-label">${e.feature.getProperty('name')}</span>`)
        this.infoWindow.setPosition(e.latLng)
        this.infoWindow.open({ map: this.map, shouldFocus: false })
      })
      this.map.data.addListener('mouseout', e => {
        this.map.data.revertStyle(e.feature)
        this.infoWindow.close()
      })
      // Contêiner pode montar oculto/sem largura (abas, collapse, v-if): reenquadra quando ganhar tamanho
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.map && this.hasSize()) this.fit()
        })
        this.resizeObserver.observe(this.$refs.map)
      }
      this.render()
    },
    async loadWorld () {
      if (worldGeoJson) return worldGeoJson
      // Carregado sob demanda para não pesar o bundle principal
      const [{ feature }, topology] = await Promise.all([
        import('topojson-client'),
        import('./world-countries.json')
      ])
      const data = topology.default || topology
      worldGeoJson = feature(data, data.objects.countries)
      return worldGeoJson
    },
    codeOf (feature) {
      return this.isBrazilOnly ? feature.getProperty('uf') : feature.getProperty('iso2')
    },
    async render () {
      if (!this.map) return
      const token = ++this.renderToken
      const geojson = this.isBrazilOnly ? brazilStates : await this.loadWorld()
      if (token !== this.renderToken || !this.map) return

      const selected = new Set(this.selectedCodes)
      this.map.data.forEach(f => this.map.data.remove(f))
      this.map.data.addGeoJson(geojson)
      this.map.data.setStyle(f => {
        if (selected.has(this.codeOf(f))) {
          return { fillColor: GOLD, fillOpacity: 0.55, strokeColor: GOLD_DARK, strokeWeight: 1, clickable: true }
        }
        // Não selecionadas ficam só como contorno discreto (Brasil) ou invisíveis (mundo)
        return {
          fillOpacity: 0,
          strokeColor: '#9aa3af',
          strokeOpacity: this.isBrazilOnly ? 0.5 : 0,
          strokeWeight: 0.5,
          clickable: false
        }
      })

      this.fit()
      this.loading = false
      await this.loadPins(token)
    },
    hasSize () {
      const el = this.$refs.map
      return !!el && el.clientWidth > 0 && el.clientHeight > 0
    },
    // Enquadra o país inteiro (Brasil) ou os países selecionados, sem cortes
    fit () {
      if (!this.map || !this.hasSize()) return
      const bounds = new this.google.maps.LatLngBounds()
      let count = 0
      this.map.data.forEach(f => {
        if (!this.selectedCodes.includes(this.codeOf(f))) return
        f.getGeometry().forEachLatLng(ll => {
          bounds.extend(ll)
          count++
        })
      })
      if (count > 0) {
        this.map.fitBounds(bounds, 16)
      } else {
        this.map.fitBounds(WORLD_BOUNDS, 16)
      }
    },
    clearMarkers () {
      (this.markers || []).forEach(m => { m.map = null })
      this.markers = []
    },
    // Tamanho proporcional à população quando o backend informa (10 a 18 px)
    pinSize (city) {
      const pop = Number(city.population)
      if (!Number.isFinite(pop) || pop <= 0) return 12
      return Math.round(Math.min(18, Math.max(10, (Math.log10(pop) - 1) * 2)))
    },
    pinLabel (city) {
      const suffix = city.state_short_name ? ` - ${city.state_short_name}` : ''
      return `${city.name}${suffix}`
    },
    pinElement (city) {
      const size = this.pinSize(city)
      const el = document.createElement('div')
      el.style.cssText = `width:${size}px;height:${size}px;border-radius:50%;background:#dc3545;` +
        'border:2px solid #fff;box-shadow:0 0 0 5px rgba(220,53,69,.2),0 1px 3px rgba(0,0,0,.35);'
      return el
    },
    async geocodeCity (geocoder, city) {
      // Coordenadas vindas do backend (geotargets.lat/lng) dispensam o geocoding do Google
      const lat = parseFloat(city.lat)
      const lng = parseFloat(city.lng)
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng }
      if (geocodeCache.has(city.id)) return geocodeCache.get(city.id)
      const address = [city.name, city.state_short_name, city.country_short_name].filter(Boolean).join(', ')
      let coords = null
      try {
        const { results } = await this.withTimeout(geocoder.geocode({ address }), GEOCODE_TIMEOUT_MS)
        if (results && results[0]) {
          const loc = results[0].geometry.location
          coords = { lat: loc.lat(), lng: loc.lng() }
        }
      } catch (e) {
        // Timeout/erro transitório: não guarda no cache para tentar de novo depois
        return null
      }
      geocodeCache.set(city.id, coords)
      return coords
    },
    async loadPins (token) {
      this.clearMarkers()
      this.pins = []
      const cities = this.cities.slice(0, MAX_PINS)
      if (cities.length === 0) return
      try {
        const geocoder = new this.google.maps.Geocoder()
        const pins = []
        for (const city of cities) {
          const coords = await this.geocodeCity(geocoder, city)
          if (token !== this.renderToken || !this.map) return
          if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) continue
          pins.push(city)
          this.markers.push(new this.google.maps.marker.AdvancedMarkerElement({
            map: this.map,
            position: coords,
            title: this.pinLabel(city),
            content: this.pinElement(city)
          }))
        }
        this.pins = pins
      } catch (e) {
        // Mapa é apenas visual: sem pins se o Google falhar
      }
    }
  }
}
</script>

<style scoped>
.map-frame {
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f4f6f9;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
  overflow: hidden;
}
.map-canvas,
.map-skeleton {
  width: 100%;
  height: 440px;
  background: #f4f6f9;
}
@media (max-width: 767px) {
  .map-canvas,
  .map-skeleton {
    height: 320px;
  }
}
.map-error {
  position: absolute;
  inset: 0;
  z-index: 1001;
  background: #f4f6f9;
  color: #4b5563;
  font-size: 0.875rem;
}
.map-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.legend-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #fff;
  color: #4b5563;
}
.map-skeleton {
  position: absolute;
  inset: 0;
  z-index: 1000;
}
.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
}
.legend-region {
  background: #d8ab44;
}
.legend-pin {
  background: #dc3545;
}
</style>

<style>
.mini-map-label {
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
}
</style>
